import React, { useEffect, useState, useMemo } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Button, Table, Input, Row, Col } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { FaEdit, FaPlus, FaSearch, FaFileExcel } from "react-icons/fa"
import { MdDelete, MdCancel } from "react-icons/md"
import { fetchData, FetchDeleteData } from "utils/Common_api_functions"
import Loader from "components/Loader/Loader"
import moment from "moment"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"
import Swal from "sweetalert2"
import * as XLSX from "xlsx"

const MONTHS = [
  { label: "All Months", value: "" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
]

const currentYear = new Date().getFullYear()
const YEARS = [
  { label: "All Years", value: "" },
  ...Array.from({ length: currentYear - 2025 + 1 }, (_, i) => ({
    label: String(2025 + i),
    value: String(2025 + i),
  })),
]

const ColdCallingIndex = () => {
  const navigate = useNavigate()

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedMonth, setSelectedMonth] = useState(
    String(moment().format("MM"))
  )
  const [selectedYear, setSelectedYear] = useState(String(moment().year()))
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [employeeList, setEmployeeList] = useState([])
  const [datePreset, setDatePreset] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  useEffect(() => {
    fetchColdCalls()
    fetchEmployees()
  }, [])

  async function fetchColdCalls() {
    const APIdata = await fetchData(setLoading, "cold/call")
    if (APIdata?.success) setData(APIdata?.data)
  }

  async function fetchEmployees() {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) setEmployeeList(APIdata?.data)
  }

  const resetPage = () => setCurrentPage(1)

  const clearPresetAndDateRange = () => {
    setDatePreset("")
    setDateFrom("")
    setDateTo("")
  }

  const applyPreset = preset => {
    setDatePreset(preset)
    setSelectedMonth("")
    setSelectedYear("")
    resetPage()

    const today = new Date()
    const fmt = d => d.toISOString().split("T")[0]

    if (preset === "") {
      setDateFrom("")
      setDateTo("")
      return
    }

    const presetMap = {
      today: [fmt(today), fmt(today)],
      yesterday: (() => {
        const d = new Date(today)
        d.setDate(d.getDate() - 1)
        return [fmt(d), fmt(d)]
      })(),
      last_7_days: (() => {
        const s = new Date(today)
        s.setDate(s.getDate() - 7)
        return [fmt(s), fmt(today)]
      })(),
      last_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 1)
        return [fmt(s), fmt(today)]
      })(),
      last_3_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 3)
        return [fmt(s), fmt(today)]
      })(),
      last_6_month: (() => {
        const s = new Date(today)
        s.setMonth(s.getMonth() - 6)
        return [fmt(s), fmt(today)]
      })(),
      last_1_year: (() => {
        const s = new Date(today)
        s.setFullYear(s.getFullYear() - 1)
        return [fmt(s), fmt(today)]
      })(),
    }

    const [from, to] = presetMap[preset]
    setDateFrom(from)
    setDateTo(to)
  }

  const filteredData = useMemo(() => {
    if (!data) return []
    let result = data

    if (selectedYear)
      result = result.filter(
        item => moment(item.date).format("YYYY") === selectedYear
      )

    if (selectedMonth)
      result = result.filter(
        item => moment(item.date).format("MM") === selectedMonth
      )

    if (dateFrom || dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null
      const to = dateTo ? new Date(dateTo) : null
      if (from) from.setHours(0, 0, 0, 0)
      if (to) to.setHours(23, 59, 59, 999)
      result = result.filter(item => {
        const d = item.date ? new Date(item.date) : null
        if (!d) return false
        if (from && d < from) return false
        if (to && d > to) return false
        return true
      })
    }

    if (employeeFilter)
      result = result.filter(item => item?.addedById === Number(employeeFilter))

    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(item =>
        [
          item.name,
          item.email,
          item.type,
          item.machineName,
          item.area,
          item.addedBy?.name,
          moment(item.date).format("DD-MM-YYYY"),
        ]
          .filter(Boolean)
          .some(v => v.toLowerCase().includes(q))
      )
    }

    return result
  }, [
    search,
    selectedMonth,
    selectedYear,
    dateFrom,
    dateTo,
    employeeFilter,
    data,
  ])

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(start, start + pageSize)

  const activeFilters = [
    selectedYear,
    selectedMonth,
    search.trim(),
    dateFrom,
    dateTo,
    datePreset,
  ].filter(Boolean).length

  // ── Excel export ────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filteredData.map((item, i) => ({
      "#": i + 1,
      Date: moment(item.date).format("DD-MM-YYYY"),
      Name: item.name || "",
      "Mobile No": item.mobileNo || "",
      Type: item.type || "",
      Discussion: item.machineName || "",
      Area: item.area || "",
      "Machine Name": item.machineName || "",
      "Added By": item.addedBy?.name || "",
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    ws["!cols"] = [
      { wch: 5 },   // #
      { wch: 14 },  // Date
      { wch: 25 },  // Name
      { wch: 15 },  // Mobile No
      { wch: 18 },  // Type
      { wch: 40 },  // Discussion
      { wch: 20 },  // Area
      { wch: 25 },  // Machine Name
      { wch: 20 },  // Added By
    ]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Cold Calling List")

    const fileName = `Cold_Calling_List_${moment().format("DD-MM-YYYY")}.xlsx`
    XLSX.writeFile(wb, fileName)
  }

  const handleDelete = (staff, id) => {
    Swal.fire({
      title: `Are you sure you want to delete ${staff?.name}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "cold/call", id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Cold Call Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          fetchColdCalls()
        } else {
          Swal.fire({
            title: "Error Deleting Cold Call.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const { checkAccess } = usePermissionData()

  return (
    <PermittedComponent isAccess={checkAccess(4, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb
          title="Cold Calling Management"
          breadcrumbItem="Cold Calling"
        />

        <Card>
          <CardBody>
            {/* ── Filters ── */}
            <Row className="g-2 mb-3 align-items-end">
              {/* Search */}
              <Col xs={12} sm={6} md={4} lg={3}>
                <div className="position-relative">
                  <FaSearch
                    style={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#aaa",
                      fontSize: 13,
                    }}
                  />
                  <Input
                    placeholder="Search name, email, type..."
                    style={{ paddingLeft: 30 }}
                    className="rounded-4"
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value)
                      resetPage()
                    }}
                  />
                  {search && (
                    <MdCancel
                      onClick={() => {
                        setSearch("")
                        resetPage()
                      }}
                      style={{
                        position: "absolute",
                        right: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#aaa",
                        fontSize: 16,
                      }}
                    />
                  )}
                </div>
              </Col>

              {/* Employee */}
              <Col xs={12} sm={6} md={4} lg={2}>
                <Input
                  type="select"
                  className="rounded-4"
                  value={employeeFilter}
                  onChange={e => {
                    setEmployeeFilter(e.target.value)
                    resetPage()
                  }}
                >
                  <option value="">All Employees</option>
                  {employeeList.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Month */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <Input
                  type="select"
                  className="rounded-4"
                  style={{
                    borderColor: selectedMonth ? "#be9545" : undefined,
                    color: selectedMonth ? "#be9545" : undefined,
                    fontWeight: selectedMonth ? 600 : undefined,
                  }}
                  value={selectedMonth}
                  onChange={e => {
                    setSelectedMonth(e.target.value)
                    clearPresetAndDateRange()
                    resetPage()
                  }}
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Year */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <Input
                  type="select"
                  className="rounded-4"
                  style={{
                    borderColor: selectedYear ? "#be9545" : undefined,
                    color: selectedYear ? "#be9545" : undefined,
                    fontWeight: selectedYear ? 600 : undefined,
                  }}
                  value={selectedYear}
                  onChange={e => {
                    setSelectedYear(e.target.value)
                    clearPresetAndDateRange()
                    resetPage()
                  }}
                >
                  {YEARS.map(y => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </Input>
              </Col>

              {/* Preset */}
              <Col xs={12} sm={4} md={3} lg={2}>
                <Input
                  type="select"
                  className="rounded-4"
                  value={datePreset}
                  onChange={e => applyPreset(e.target.value)}
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last_7_days">Last 7 Days</option>
                  <option value="last_month">Last Month</option>
                  <option value="last_3_month">Last 3 Months</option>
                  <option value="last_6_month">Last 6 Months</option>
                  <option value="last_1_year">Last 1 Year</option>
                </Input>
              </Col>

              {/* Date From */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold text-nowrap">
                    From
                  </small>
                  <Input
                    type="date"
                    className="rounded-4"
                    value={dateFrom}
                    onChange={e => {
                      setDateFrom(e.target.value)
                      setDatePreset("")
                      setSelectedMonth("")
                      setSelectedYear("")
                      resetPage()
                    }}
                  />
                </div>
              </Col>

              {/* Date To */}
              <Col xs={6} sm={4} md={3} lg={2}>
                <div className="d-flex align-items-center gap-1">
                  <small className="text-muted fw-semibold text-nowrap">
                    To
                  </small>
                  <Input
                    type="date"
                    className="rounded-4"
                    value={dateTo}
                    onChange={e => {
                      setDateTo(e.target.value)
                      setDatePreset("")
                      setSelectedMonth("")
                      setSelectedYear("")
                      resetPage()
                    }}
                  />
                </div>
              </Col>

              {/* Page size */}
              <Col xs={6} sm={4} md={2} lg={1}>
                <Input
                  type="select"
                  className="rounded-4"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    resetPage()
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </Col>

              {/* Action buttons */}
              <Col
                xs={12}
                sm="auto"
                className="ms-sm-auto d-flex gap-2 flex-wrap justify-content-end"
              >
                <Button
                  color="success"
                  className="rounded-4"
                  onClick={exportToExcel}
                  disabled={filteredData.length === 0}
                  title="Export filtered data to Excel"
                >
                  <FaFileExcel className="me-1" /> Export
                </Button>
                {checkAccess(4, "add") && (
                  <Button
                    className="btn-blue rounded-4"
                    onClick={() => navigate("/cold-calling/add")}
                  >
                    <FaPlus className="me-1" /> Add Cold Call
                  </Button>
                )}
              </Col>
            </Row>

            {/* ── Table ── */}
            <Table bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Mobile No</th>
                  <th>Type</th>
                  <th>Discussion</th>
                  <th>Area</th>
                  <th>Location</th>
                  <th>Added By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td className="text-center py-4" colSpan={10}>
                      {activeFilters > 0
                        ? "No results found for the selected filters"
                        : "No Cold Call Found"}
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{start + index + 1}</td>
                      <td>{moment(item.date).format("DD-MM-YYYY")}</td>
                      <td>{item.name}</td>
                      <td>{item?.mobileNo || "--"}</td>
                      <td className="text-capitalize">{item.type}</td>
                      <td>{item.machineName}</td>
                      <td>{item.area}</td>
                      <td style={{ maxWidth: 220, wordBreak: "break-word", whiteSpace: "normal", fontSize: 12 }}>
                        {item?.location || ""}
                      </td>
                      <td>{item.addedBy?.name}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {checkAccess(4, "edit") && (
                          <Button
                            color="info"
                            size="sm"
                            onClick={() =>
                              navigate(`/cold-calling/edit/${item.id}`)
                            }
                          >
                            <FaEdit />
                          </Button>
                        )}
                        {checkAccess(4, "delete") && (
                          <Button
                            color="danger"
                            size="sm"
                            className="ms-2"
                            onClick={() => handleDelete(item, item?.id)}
                          >
                            <MdDelete />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>

            {/* ── Pagination ── */}
            <nav className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
              <h6 className="mb-0">
                Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {filteredData.length} entries
              </h6>

              <ul className="pagination mb-0 flex-wrap">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  >
                    Previous
                  </button>
                </li>
                {(() => {
                  const window = 5
                  let start = Math.max(1, currentPage - Math.floor(window / 2))
                  let end = start + window - 1
                  if (end > totalPages) {
                    end = totalPages
                    start = Math.max(1, end - window + 1)
                  }
                  return Array.from(
                    { length: end - start + 1 },
                    (_, i) => start + i
                  ).map(p => (
                    <li
                      key={p}
                      className={`page-item ${
                        p === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))
                })()}
                <li
                  className={`page-item ${
                    currentPage === totalPages || totalPages === 0
                      ? "disabled"
                      : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(p => Math.min(p + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>
      </div>
    </PermittedComponent>
  )
}

export default ColdCallingIndex
